import i18n from '../i18n';

/**
 * Utility functions for managing language preferences
 */

export const SUPPORTED_LANGUAGES = ['en', 'tc', 'sc'];

/**
 * Normalize any language code to a supported app language, or null if unknown.
 * @param {string|null|undefined} languageCode
 * @returns {string|null}
 */
export const normalizeLanguage = (languageCode) => {
    if (!languageCode || typeof languageCode !== 'string') return null;

    const raw = languageCode.trim().toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(raw)) return raw;

    const base = raw.split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(base)) return base;

    // Browser / legacy Chinese codes
    if (raw.startsWith('zh')) {
        if (raw.includes('cn') || raw.includes('hans') || raw === 'zh-sg') return 'sc';
        return 'tc';
    }

    return null;
};

/**
 * Read the last language choice persisted for this browser.
 * @returns {string|null}
 */
export const getStoredLanguage = () => {
    try {
        return normalizeLanguage(localStorage.getItem('i18nextLng'));
    } catch {
        return null;
    }
};

/**
 * Pick the language that best matches user intent.
 * localStorage is preferred when present (last explicit choice on this device);
 * DB profile is next (cross-device); then the active i18n language.
 *
 * @param {{ dbLanguage?: string|null, localLanguage?: string|null, i18nLanguage?: string|null }} sources
 * @returns {string}
 */
export const resolveLanguagePreference = ({
    dbLanguage = null,
    localLanguage = null,
    i18nLanguage = null
} = {}) => {
    const local = normalizeLanguage(localLanguage ?? getStoredLanguage());
    const db = normalizeLanguage(dbLanguage);
    const active = normalizeLanguage(i18nLanguage ?? i18n.language);

    if (local) return local;
    if (db) return db;
    if (active) return active;
    return 'en';
};

/**
 * Keep the cached user object in localStorage aligned with language changes.
 * @param {string} languageCode
 */
const syncCachedUserLanguage = (languageCode) => {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return;
        if (parsed.preferred_language === languageCode) return;
        localStorage.setItem('user', JSON.stringify({
            ...parsed,
            preferred_language: languageCode
        }));
    } catch {
        // Ignore corrupt cache
    }
};

/**
 * Get the current language preference (normalized).
 * @returns {string} The current language code
 */
export const getCurrentLanguage = () => {
    return resolveLanguagePreference({
        localLanguage: getStoredLanguage(),
        i18nLanguage: i18n.language
    });
};

/**
 * Set language preference and update both i18n and user profile if authenticated
 * @param {string} languageCode - The language code to set
 * @param {Object} userTable - Database handler instance (optional, for authenticated users)
 * @param {string} userId - User ID (optional, for authenticated users)
 * @param {Function} updateLanguagePreference - Function from AuthContext to update language locally
 * @returns {Promise<boolean>} Success status
 */
export const setLanguagePreference = async (languageCode, userTable = null, userId = null, updateLanguagePreference = null) => {
    try {
        const normalized = normalizeLanguage(languageCode);
        if (!normalized) {
            console.error('Unsupported language code:', languageCode);
            return false;
        }

        // Update i18n immediately for responsive UI (also writes i18nextLng)
        await i18n.changeLanguage(normalized);

        // Clear the language initialization flag to allow reload mechanism to work
        localStorage.removeItem('languageInitialized');

        // Keep cached user profile in sync so refresh does not reintroduce a stale DB value
        syncCachedUserLanguage(normalized);

        // Update language preference locally in AuthContext if available
        if (updateLanguagePreference) {
            updateLanguagePreference(normalized);
        }

        // Store in user profile if authenticated
        if (userTable && userId) {
            try {
                await userTable.handleUpdate(
                    { User_ID: userId },
                    { preferred_language: normalized },
                    false // Don't show notification for language change
                );
            } catch (error) {
                console.error('Error saving language preference to profile:', error);
                // Don't throw error - language change still works locally
            }
        }

        return true;
    } catch (error) {
        console.error('Error setting language preference:', error);
        return false;
    }
};

/**
 * Load language preference from user profile, preferring a fresher local choice when present.
 * @param {Object} userData - User data from database
 * @param {Array} supportedLanguages - Array of supported language codes
 * @returns {Promise<string>} The language code to use
 */
export const loadLanguageFromProfile = async (userData, supportedLanguages = SUPPORTED_LANGUAGES) => {
    const resolved = resolveLanguagePreference({
        dbLanguage: userData?.preferred_language,
        localLanguage: getStoredLanguage(),
        i18nLanguage: i18n.language
    });

    const languageCode = supportedLanguages.includes(resolved) ? resolved : 'en';
    await i18n.changeLanguage(languageCode);
    syncCachedUserLanguage(languageCode);
    return languageCode;
};

/**
 * Get language display info
 * @param {string} languageCode - The language code
 * @returns {Object} Language info object with code, name, and flag
 */
export const getLanguageInfo = (languageCode) => {
    const languages = getSupportedLanguages();
    const normalized = normalizeLanguage(languageCode) || 'en';
    return languages.find(lang => lang.code === normalized) || languages[0];
};

/**
 * Get all supported languages
 * @returns {Array} Array of language objects
 */
export const getSupportedLanguages = () => {
    return [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'tc', name: '繁體中文', flag: '🇭🇰' },
        { code: 'sc', name: '简体中文', flag: '🇨🇳' }
    ];
};
